import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MessageSquarePlus, MessagesSquare, Search, SearchX, TriangleAlert } from "lucide-react";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb, TspEmptyState, TspSkeleton } from "../../components/primitives";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import useTspForumData from "./useTspForumData";
import { FORUM_SORTS, TOPIC_FILTERS, TOPIC_PAGE_SIZE } from "./forumModel";
import TopicRow from "./components/TopicRow";
import TopicDetail from "./components/TopicDetail";
import TopicFormModal from "./components/TopicFormModal";

const NO_FILTERS = { search: "", category: "", filter: "all", sort: "latest" };

// Forum (/tsp/forum): discussions and questions between the parties, in ten
// categories, with replies, likes, helpful / best-answer marks, pinning,
// reporting and subscriptions. A topic opens in place at /tsp/forum?topic=<id>.
// Search/category/type/pinned-first/pagination all happen server-side (see
// forumModel.js#topicListParams and GET /api/tsp/forum/topics) — this page
// only accumulates pages for "Load more" and never filters in React.
// Reading needs forum.view; posting, replying and reacting need forum.create;
// pin/report-clear/best-answer depend on the topic and come from its own
// `capabilities` (see TopicDetail.jsx).
function ForumContent() {
  const { hasPermission } = useTspPermission();
  const [searchParams] = useSearchParams();
  const openTopicId = searchParams.get("topic");

  const canParticipate = hasPermission(TSP_PERMISSIONS.FORUM_CREATE);

  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(NO_FILTERS);
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState([]);
  const [formOpen, setFormOpen] = useState(false);

  // Debounced the same way Media Gallery's search box is.
  useEffect(() => {
    const timer = setTimeout(() => setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput })), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, loading, error, openTopicError, actions } = useTspForumData({ ...filters, page, limit: TOPIC_PAGE_SIZE }, openTopicId);

  // Accumulate pages for "Load more"; a filter change (which always resets
  // `page` to 1) replaces the list instead of appending to it.
  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => (page === 1 ? data.topics : [...prev, ...data.topics.filter((topic) => !prev.some((existing) => existing.id === topic.id))]));
  }, [data, page]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };
  const clearFilters = () => {
    setSearchInput("");
    setFilters(NO_FILTERS);
    setPage(1);
  };
  const hasActiveFilters = Boolean(filters.search || filters.category || filters.filter !== "all");

  const categories = data?.categories || [];
  const categoryByKey = useMemo(() => Object.fromEntries(categories.map((category) => [category.key, category])), [categories]);
  const totalTopics = useMemo(() => categories.reduce((sum, category) => sum + (category.count || 0), 0), [categories]);
  const openTopic = data?.openTopic || null;

  // Count a view once each time a topic is opened (guarded against re-runs).
  const countedRef = useRef(null);
  useEffect(() => {
    if (!openTopic || countedRef.current === openTopic.id) return;
    countedRef.current = openTopic.id;
    actions.countView(openTopic.id);
  }, [openTopic, actions]);

  const handlePost = async (values) => {
    const entry = await actions.addTopic(values);
    notification("success", `“${entry.title}” posted to the forum.`);
    setFormOpen(false);
    clearFilters();
  };

  const heading = (
    <div className="tsp-page-heading-row">
      <div>
        <h1>Forum</h1>
        <TspBreadcrumb items={openTopic ? ["Dashboard", { label: "Forum", to: "/tsp/forum" }, "Topic"] : ["Dashboard", "Forum"]} />
      </div>
      {canParticipate && (
        <button type="button" className="tsp-btn tsp-btn-success tsp-btn-pill" onClick={() => setFormOpen(true)}>
          <MessageSquarePlus size={15} aria-hidden="true" /> Start New Topic
        </button>
      )}
    </div>
  );

  if (loading && !data) {
    return (
      <div className="tsp-page tsp-forum-page">
        {heading}
        <TspSkeleton count={5} variant="row" label="Loading forum" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tsp-page tsp-forum-page">
        {heading}
        <TspEmptyState
          tone="error"
          icon={TriangleAlert}
          title="Could not load the forum"
          message="Something went wrong while loading topics. Please try again."
          action={
            <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={() => window.location.reload()}>
              Try again
            </button>
          }
        />
      </div>
    );
  }

  // A topic link that no longer resolves (removed, or never existed).
  if (openTopicId && openTopicError) {
    return (
      <div className="tsp-page tsp-forum-page">
        {heading}
        <TspEmptyState
          icon={SearchX}
          title="Topic not found"
          message="It may have been removed, or the link is incorrect."
          action={
            <Link to="/tsp/forum" className="tsp-btn tsp-btn-outline-primary">
              Back to all topics
            </Link>
          }
        />
      </div>
    );
  }

  const remaining = (data?.meta?.total || 0) - accumulated.length;

  return (
    <div className="tsp-page tsp-forum-page">
      {heading}

      {openTopicId ? (
        openTopic && (
          <TopicDetail
            topic={openTopic}
            category={categoryByKey[openTopic.category]}
            canParticipate={canParticipate}
            actions={actions}
            onReported={(reported) => notification("success", `“${reported.title}” was reported to the moderators.`)}
          />
        )
      ) : totalTopics === 0 ? (
        <TspEmptyState icon={MessagesSquare} title="No topics yet" message="Start the first discussion or ask a question for the other parties." />
      ) : (
        <div className="tsp-forum-layout">
          <aside className="tsp-forum-categories" aria-label="Categories">
            <h2>Categories</h2>
            <ul>
              <li>
                <button type="button" aria-pressed={filters.category === ""} onClick={() => setFilter("category", "")}>
                  All Topics <span>{totalTopics}</span>
                </button>
              </li>
              {categories.map((category) => (
                <li key={category.key}>
                  <button type="button" aria-pressed={filters.category === category.key} onClick={() => setFilter("category", filters.category === category.key ? "" : category.key)}>
                    {category.label} <span>{category.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="tsp-forum-main">
            <div className="tsp-journey-filters" role="search">
              <label className="tsp-journey-search">
                <Search size={16} aria-hidden="true" />
                <input type="search" placeholder="Search topics by title, text or author…" aria-label="Search forum topics" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
              </label>
              <select className="tsp-select tsp-filter-select" aria-label="Sort topics" value={filters.sort} onChange={(event) => setFilter("sort", event.target.value)}>
                {FORUM_SORTS.map((sort) => (
                  <option key={sort.value} value={sort.value}>
                    {sort.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="tsp-chip-filter-row" role="group" aria-label="Filter topics">
              {TOPIC_FILTERS.map((option) => (
                <button key={option.value} type="button" className="tsp-chip-filter" aria-pressed={filters.filter === option.value} onClick={() => setFilter("filter", option.value)}>
                  {option.label}
                </button>
              ))}
            </div>

            <div className="tsp-results-bar">
              <span className="tsp-muted-text" aria-live="polite">
                Showing {accumulated.length} of {data?.meta?.total ?? 0} {data?.meta?.total === 1 ? "topic" : "topics"}
              </span>
              {hasActiveFilters && (
                <button type="button" className="tsp-filter-clear" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>

            {accumulated.length === 0 ? (
              <TspEmptyState
                icon={SearchX}
                title="No topics match your filters"
                message="Try a different search, category or filter, or clear them."
                action={
                  <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={clearFilters}>
                    Clear filters
                  </button>
                }
              />
            ) : (
              <>
                <ul className="tsp-topic-list">
                  {accumulated.map((entry) => (
                    <TopicRow key={entry.id} topic={entry} category={categoryByKey[entry.category]} />
                  ))}
                </ul>
                {remaining > 0 && (
                  <div className="tsp-load-more">
                    <button type="button" className="tsp-btn tsp-btn-outline-primary" disabled={loading} onClick={() => setPage((prev) => prev + 1)}>
                      {loading ? "Loading…" : `Load more (${remaining} remaining)`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {formOpen && <TopicFormModal categories={categories} defaultCategory={filters.category} onClose={() => setFormOpen(false)} onSubmit={handlePost} />}
    </div>
  );
}

// The toast container lives outside the loading/error/loaded branches so it is
// never remounted (which would drop a queued toast).
export default function TspForumPage() {
  return (
    <>
      <ToastContainer />
      <ForumContent />
    </>
  );
}
